import { useCallback, useEffect, useState } from 'react';
import {
  deleteUnionNotice,
  deleteUnionNoticeComment,
  loadUnionNotices,
  saveUnionNotice,
  saveUnionNoticeComment,
} from '../api/unionNoticesApi.js';

const EMPTY = {
  unionId: '',
  unionName: '',
  viewerRole: '',
  viewerUserId: '',
  viewerNickname: '',
  notices: [],
};

function notifyNoticeChanged() {
  window.dispatchEvent(new CustomEvent('doroparty:notice-changed'));
}

export function useUnionNotices() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = useCallback(async ({ preserveNotice = false } = {}) => {
    setLoading(true);
    try {
      setData(await loadUnionNotices());
      if (!preserveNotice) setNotice({ type: '', message: '' });
    } catch (error) {
      setData(EMPTY);
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '공지를 불러오지 못했습니다.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadUnionNotices().then((result) => { if (active) setData(result); }).catch((error) => {
      if (active) setNotice({ type: 'error', message: error instanceof Error ? error.message : '공지를 불러오지 못했습니다.' });
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const save = useCallback(async (input) => {
    if (working) return false;
    setWorking('save');
    setNotice({ type: '', message: '' });
    try {
      await saveUnionNotice(input);
      setData(await loadUnionNotices());
      notifyNoticeChanged();
      setNotice({ type: 'success', message: '공지를 저장했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '공지를 저장하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [working]);

  const remove = useCallback(async (id) => {
    if (working) return false;
    setWorking(`delete:${id}`);
    setNotice({ type: '', message: '' });
    try {
      await deleteUnionNotice(id);
      setData(await loadUnionNotices());
      notifyNoticeChanged();
      setNotice({ type: 'success', message: '공지를 삭제했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '공지를 삭제하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [working]);

  const saveComment = useCallback(async (input) => {
    if (working) return false;
    const noticeId = String(input?.noticeId || '');
    setWorking(`comment-save:${noticeId}`);
    setNotice({ type: '', message: '' });
    try {
      await saveUnionNoticeComment(input);
      setData(await loadUnionNotices());
      setNotice({ type: 'success', message: input?.id ? '댓글을 수정했습니다.' : '댓글을 등록했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '댓글을 저장하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [working]);

  const removeComment = useCallback(async (id) => {
    if (working) return false;
    setWorking(`comment-delete:${id}`);
    setNotice({ type: '', message: '' });
    try {
      await deleteUnionNoticeComment(id);
      setData(await loadUnionNotices());
      setNotice({ type: 'success', message: '댓글을 삭제했습니다.' });
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '댓글을 삭제하지 못했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [working]);

  return {
    ...data,
    loading,
    working,
    notice,
    refresh,
    save,
    remove,
    saveComment,
    removeComment,
  };
}
